import { useConvexAuth, useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { authClient } from '../lib/auth-client';
import type { Id } from '../../convex/_generated/dataModel';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { features } from '../config/features';

export interface ConvexUser {
  _id: Id<"users">;
  authUserId?: string;
  username: string;
  email?: string;
  email2?: string;
  email2Verified?: boolean;
  email2VerifiedTillDate?: number;
  name?: string;
  role: 'customer' | 'admin' | 'super_admin';
  emailVerified: boolean;
  mobileVerified: boolean;
  mobileVerifiedTillDate?: number;
  lastDetailsUpdate: number;
  mobileNumber?: string;
  telephoneNumber?: string;
  address?: string;
  mustChangePassword?: boolean;
  passwordExpiresAt?: number;
  contactVerificationRequired?: boolean;
  tariffPencePerKwh?: number;
  tariffSource?: string;
  tariffUpdatedAt?: number;
  termsConditionsAcceptedAt?: number;
  termsConditionsNextRequiredAt?: number;
  termsConditionsVersionAccepted?: boolean;
}

export interface AuthState {
  isSignedIn: boolean;
  isLoaded: boolean;
  isSignedOut: boolean;
  convexUser: ConvexUser | null | undefined;
  isLoadingProfile: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  requiresPasswordChange: boolean;
  requiresContactVerification: boolean;
  requiresTermsConsent: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => void;
  signIn: () => void;
}

/**
 * Combined hook for Better Auth + Convex user profile.
 */
export function useAppAuth(): AuthState {
  const { isSignedOut, setIsSignedOut } = useAuth();
  const { isAuthenticated: isConvexAuthenticated, isLoading: isConvexLoading } = useConvexAuth();
  const navigate = useNavigate();
  const isLoaded = !isConvexLoading;
  const isSignedIn = isConvexAuthenticated && !isSignedOut;

  // Get the current user identity from Convex auth
  const identity = useQuery(
    api.authFunctions.getCurrentUser,
    isSignedIn ? {} : "skip"
  );

  // Look up the app user by identity subject (Better Auth user ID)
  const convexUser = useQuery(
    api.users.getByAuthUserId,
    identity?.subject ? { authUserId: identity.subject } : "skip"
  );

  // Auto-link profile on first sign-in
  const linkOrCreate = useMutation(api.authFunctions.linkOrCreateProfile);
  const linkAttempted = useRef(false);
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    // If signed in, have identity, but no convexUser found, try to link
    if (isSignedIn && identity && convexUser === null && !linkAttempted.current && !isLinking) {
      linkAttempted.current = true;
      setIsLinking(true);
      linkOrCreate()
        .catch(console.error)
        .finally(() => setIsLinking(false));
    }
  }, [isSignedIn, identity, convexUser, linkOrCreate, isLinking]);

  // Reset link attempt when user changes
  useEffect(() => {
    if (!isSignedIn) {
      linkAttempted.current = false;
      setIsLinking(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    // Clear temporary sign-out override once auth state has caught up.
    if (isSignedOut && !isConvexAuthenticated) {
      setIsSignedOut(false);
    }
  }, [isSignedOut, isConvexAuthenticated, setIsSignedOut]);

  const isLoadingProfile = (identity === undefined && isSignedIn) || (convexUser === undefined && isSignedIn) || isLinking;

  const isAuthenticated = isSignedIn && !isSignedOut && !!convexUser;
  const isAdmin = convexUser?.role === 'admin' || convexUser?.role === 'super_admin';
  const isSuperAdmin = convexUser?.role === 'super_admin';

  const requiresPasswordChange = useMemo(() => {
    if (!convexUser) return false;
    if (convexUser.mustChangePassword) return true;
    if (convexUser.passwordExpiresAt && convexUser.passwordExpiresAt < Date.now()) return true;
    return false;
  }, [convexUser]);

  const requiresContactVerification = useMemo(() => {
    if (!convexUser || convexUser.role !== 'customer') return false;
    // Rollout controls: global kill-switch plus per-user opt-in. Users are only
    // gated once contactVerificationRequired has been set true on their record.
    if (!features.contactVerificationEnabled) return false;
    if (convexUser.contactVerificationRequired !== true) return false;

    const now = Date.now();
    const hasMobile = Boolean(convexUser.mobileNumber?.trim());
    const hasSecondaryEmail = Boolean(convexUser.email2?.trim());
    const mobileCurrent =
      !!convexUser.mobileVerified &&
      !!convexUser.mobileVerifiedTillDate &&
      convexUser.mobileVerifiedTillDate > now;
    const secondaryEmailCurrent =
      !!convexUser.email2Verified &&
      !!convexUser.email2VerifiedTillDate &&
      convexUser.email2VerifiedTillDate > now;
    const mobileExpired = hasMobile && (
      !mobileCurrent
    );

    const emailExpired = hasSecondaryEmail && (
      !secondaryEmailCurrent
    );

    return (
      mobileExpired ||
      emailExpired ||
      (!hasMobile && !hasSecondaryEmail && !mobileCurrent && !secondaryEmailCurrent)
    );
  }, [convexUser]);

  const requiresTermsConsent = useMemo(() => {
    if (!convexUser) return false;
    if (!convexUser.termsConditionsAcceptedAt) return true;
    const now = Date.now();
    if (convexUser.termsConditionsNextRequiredAt && convexUser.termsConditionsNextRequiredAt <= now) return true;
    return false;
  }, [convexUser]);

  const signOut = async (): Promise<void> => {
    setIsSignedOut(true);
    const result = await authClient.signOut();

    if (result?.error) {
      setIsSignedOut(false);
      throw new Error(result.error.message || 'Sign out failed');
    }

    navigate('/login', { replace: true });
  };

  return {
    isSignedIn,
    isLoaded,
    isSignedOut,
    convexUser: convexUser as ConvexUser | null | undefined,
    isLoadingProfile,
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    requiresPasswordChange,
    requiresContactVerification,
    requiresTermsConsent,
    signIn: () => { window.location.href = '/login'; },
    signOut,
    refreshProfile: () => { },
  };
}

// Named export used across the app.
export { useAppAuth as useBetterAuthConvexAuth };
